/**
 * Shared trip device-sync pull via canonicalTripId (Phase 2 / Phase 4 web).
 */

import { API_BASE_URL, buildAuthHeaders } from './apiClient';
import type { TripMembership } from './tripShareService';

export const SHARED_OVERLAY_KEYS = ['accessRole', 'canEdit', 'canonicalTripId'] as const;

export function parseCanonicalTripId(
  canonicalTripId: string
): { authorSub: string; tripId: string } | null {
  const parts = canonicalTripId.split('#');
  if (parts.length !== 3 || parts[1] !== 'trip' || !parts[0] || !parts[2]) return null;
  return { authorSub: parts[0], tripId: parts[2] };
}

export function stripOverlays<T extends Record<string, unknown>>(body: T): T {
  const copy = { ...body };
  for (const key of SHARED_OVERLAY_KEYS) {
    delete copy[key];
  }
  return copy;
}

export function overlayValues(body: Record<string, unknown>): {
  accessRole: string | null;
  canEdit: boolean | null;
  canonicalTripId: string | null;
} {
  const roleRaw = body.accessRole != null ? String(body.accessRole).trim().toLowerCase() : '';
  return {
    accessRole: roleRaw || null,
    canEdit: typeof body.canEdit === 'boolean' ? body.canEdit : null,
    canonicalTripId:
      body.canonicalTripId != null && String(body.canonicalTripId).trim()
        ? String(body.canonicalTripId).trim()
        : null,
  };
}

export function lastSyncStorageKey(canonicalTripId: string): string {
  return `sharedTripSync.lastSyncAt.${canonicalTripId}`;
}

export function authorPrefixedTripId(authorSub: string, tripId: string): string {
  return `${authorSub}#trip#${tripId.toLowerCase()}`;
}

/**
 * Resolve canonical share id from membership fields.
 * Prefer server `canonicalTripId`; fall back to `authorSub#trip#{tripId}`.
 */
export function resolveMembershipCanonicalTripId(membership: {
  canonicalTripId?: string | null;
  authorSub?: string | null;
  tripId?: string | null;
}): string | null {
  const raw = String(membership.canonicalTripId ?? '').trim();
  if (raw && parseCanonicalTripId(raw)) return raw;
  const author = String(membership.authorSub ?? '').trim();
  let tripId = String(membership.tripId ?? '').trim();
  if (!author || !tripId) return null;
  // tripId may already be author#trip#uuid
  const fromTrip = parseCanonicalTripId(tripId);
  if (fromTrip) {
    return authorPrefixedTripId(fromTrip.authorSub || author, fromTrip.tripId);
  }
  // Bare uuid (or suffix after last #trip#)
  const marker = '#trip#';
  const idx = tripId.toLowerCase().lastIndexOf(marker);
  if (idx >= 0) tripId = tripId.slice(idx + marker.length);
  tripId = tripId.trim().toLowerCase();
  if (!tripId) return null;
  return authorPrefixedTripId(author, tripId);
}

export type DeviceSyncChangeRecord = {
  id: string;
  type: string;
  body: Record<string, unknown>;
  updatedAt?: string | null;
};

export async function fetchSharedTripChanges(
  canonicalTripId: string,
  since?: string | null
): Promise<DeviceSyncChangeRecord[]> {
  const headers = await buildAuthHeaders();
  const all: DeviceSyncChangeRecord[] = [];
  let cursor: string | null = null;
  do {
    const params = new URLSearchParams({
      limit: '100',
      canonicalTripId,
    });
    if (since) params.set('since', since);
    if (cursor) params.set('cursor', cursor);
    const res = await fetch(`${API_BASE_URL}/devicesync/changes?${params}`, {
      method: 'GET',
      headers,
    });
    if (res.status === 403) {
      throw new Error('SHARED_TRIP_FORBIDDEN');
    }
    if (!res.ok) {
      throw new Error(`Shared sync failed (${res.status})`);
    }
    const json = (await res.json()) as { records?: DeviceSyncChangeRecord[] };
    all.push(...(json.records ?? []));
    cursor = res.headers.get('Next-Cursor')?.trim() || null;
  } while (cursor);
  return all;
}

/** Apply overlays from trip-v1 records onto a local trip-shaped object. */
export function applyOverlaysToTripDraft(
  draft: Record<string, unknown>,
  records: DeviceSyncChangeRecord[],
  fallbackCanonical: string
): Record<string, unknown> {
  const tripRecords = records.filter((r) => r.type === 'trip-v1');
  const latest = tripRecords[tripRecords.length - 1];
  const next = { ...draft };
  next.canonicalTripId = fallbackCanonical;
  if (!latest) return next;
  const overlays = overlayValues(latest.body ?? {});
  if (overlays.canonicalTripId) next.canonicalTripId = overlays.canonicalTripId;
  if (overlays.accessRole) next.accessRole = overlays.accessRole;
  if (overlays.canEdit != null) next.canEdit = overlays.canEdit;
  else if (overlays.accessRole === 'viewer') next.canEdit = false;
  return next;
}

export type PullActiveSharedOptions = {
  /**
   * Trip ids already in the invitee's local store (lowercase UUID).
   * When the shared trip is missing locally, force a full pull (ignore lastSyncAt)
   * so an earlier empty/broken pull cannot strand the invitee forever.
   */
  localTripIds?: Set<string>;
};

export async function pullActiveSharedMemberships(
  memberships: TripMembership[],
  options: PullActiveSharedOptions = {}
): Promise<{ canonicalTripId: string; records: DeviceSyncChangeRecord[] }[]> {
  const localTripIds = options.localTripIds ?? new Set<string>();
  const results: { canonicalTripId: string; records: DeviceSyncChangeRecord[] }[] = [];

  for (const m of memberships) {
    if (String(m.status ?? '').trim().toLowerCase() !== 'active') continue;
    const canonicalTripId = resolveMembershipCanonicalTripId(m);
    if (!canonicalTripId) continue;
    const parsed = parseCanonicalTripId(canonicalTripId);
    if (!parsed) continue;

    const tripKey = parsed.tripId.toLowerCase();
    const missingLocally = !Array.from(localTripIds).some(
      (id) => String(id).trim().toLowerCase() === tripKey
    );

    // Full history when the trip is not local yet (first web sign-in after iOS accept,
    // or after a prior pull advanced lastSyncAt without persisting the trip).
    let since: string | null = null;
    if (!missingLocally) {
      since = localStorage.getItem(lastSyncStorageKey(canonicalTripId));
    } else {
      localStorage.removeItem(lastSyncStorageKey(canonicalTripId));
    }

    try {
      const records = await fetchSharedTripChanges(canonicalTripId, since);
      results.push({ canonicalTripId, records });

      // Only advance the incremental cursor when the trip is already local.
      // If it was missing, persist must succeed first (otherwise a full pull that
      // fails to materialize would strand the invitee on empty incremental pulls).
      if (records.length > 0 && !missingLocally) {
        const maxUpdated = records
          .map((r) => r.updatedAt)
          .filter(Boolean)
          .sort()
          .at(-1);
        if (maxUpdated) {
          localStorage.setItem(lastSyncStorageKey(canonicalTripId), maxUpdated);
        }
      } else if (missingLocally && records.length === 0) {
        console.warn(
          'sharedTripSync: full pull returned no records for missing shared trip',
          canonicalTripId
        );
      }
    } catch (e) {
      if (e instanceof Error && e.message === 'SHARED_TRIP_FORBIDDEN') {
        localStorage.removeItem(lastSyncStorageKey(canonicalTripId));
      }
      throw e;
    }
  }
  return results;
}
